import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    // New <media-gallery> must select the variant slide after its slideshow finishes init.
    queueMicrotask(() => {
      if (newMediaGallery instanceof MediaGallery && newMediaGallery.isConnected) {
        void newMediaGallery.activateVariantMedia(event.detail.resource);
      }
    });
  };

  /**
   * @param {import('@theme/events').VariantUpdateEvent['detail']['resource']} resource
   */
  async activateVariantMedia(resource) {
    if (!this.isConnected) return;

    await customElements.whenDefined('slideshow-component');

    // Scroller exists before slideshow rAF init; wait for both.
    for (let attempt = 0; attempt < 100; attempt++) {
      const slideshow = this.slideshow;
      if (slideshow?.isConnected && slideshow.refs?.scroller) break;
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }

    const slideshow = this.slideshow;
    if (!slideshow) return;

    await slideshow.whenInitComplete();

    if (!this.isConnected || !slideshow.isConnected) return;

    // Match variant featured media to a slide (Liquid should sort it first; this is the JS fallback).
    const featuredId = resource?.featured_media?.id ?? resource?.featured_image?.id ?? null;
    const slides = slideshow.slides ?? [];
    let index = 0;

    if (featuredId != null) {
      const mediaId = String(featuredId);

      for (let i = 0; i < slides.length; i++) {
        if (slides[i]?.querySelector(`[data-media-id="£{mediaId}"]`)) {
          index = i;
          break;
        }
      }
    }

    const slideId = slides[index]?.getAttribute('slide-id');
    const options = { animate: false, scrollViewport: false };

    if (slideId) {
      slideshow.select({ id: slideId }, undefined, options);
      return;
    }

    slideshow.select(index, undefined, options);
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false, scrollViewport: true });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
